import { useState, useEffect, useContext } from "react";
import axios from "axios";
import Preloader from "../../../shared/PreLoaders";
import { logActivity } from "../../../utils/LogActivity";
import { AuthContext } from "../../../Context/AuthContext";
import { Authorization } from "../../../Context/Authorization";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const DailyLoanReport = () => {
  const [vfc, setVfc] = useState("");
  const [vfcName, setVfcName] = useState("");
  const [date, setDate] = useState("");
  const [loanData, setLoanData] = useState({ individualLoans: 0, groupLoans: 0, totalLoans: 0 });
  const [vfcOptions, setVfcOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const Api = process.env.REACT_APP_API;
  const { userId, selectedJfmcType } = useContext(AuthContext);

  const fetchVfcNames = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId }, config);
      const data = response.data.vfcs;
      if (Array.isArray(data)) setVfcOptions(data);
      else console.error("API did not return an array", data);
    } catch (error) {
      console.log("Error fetching Data", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedJfmcType) fetchVfcNames(selectedJfmcType._id);
  }, [selectedJfmcType]);

  const fetchLoanData = async () => {
    if (!vfc || !date) return;
    setLoading(true);
    try {
      const config = Authorization();
      const response = await axios.post(
        `${Api}/api/loan-collections/daily-loan-report`,
        { vfcId: vfc, date },
        config
      );
      const data = response.data;
      setLoanData({
        individualLoans: data.individualLoan,
        groupLoans: data.groupLoan,
        totalLoans: data.totalLoan,
      });
      setHasFetched(true);
    } catch (error) {
      console.error("Error fetching loan data", error);
    }
    setLoading(false);
    await logActivity(userId, "Visited Daily Loan Report", "DailyLoanReport Page", Api);
  };

  const handleVfcChange = (e) => {
    const selectedVfcId = e.target.value;
    const selectedVfc = vfcOptions.find((option) => option._id === selectedVfcId);
    setVfc(selectedVfcId);
    setVfcName(selectedVfc ? selectedVfc.vfcName : "");
    setHasFetched(false);
  };

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Daily Loan Report</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Loan</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Daily Loan Report</span>
        </div>
      </div>

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-chart-bar" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Filter Report</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 16, alignItems: "flex-end" }}>
              <div>
                <label style={labelStyle}>Select JFMC</label>
                <select value={vfc} onChange={handleVfcChange} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                  <option value="">— Select JFMC —</option>
                  {vfcOptions.map((opt) => (
                    <option key={opt._id} value={opt._id}>{opt.vfcName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Select Date</label>
                <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setHasFetched(false); }} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
              </div>
              <div>
                <button onClick={fetchLoanData} disabled={!vfc || !date}
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: (!vfc || !date) ? "not-allowed" : "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)", opacity: (!vfc || !date) ? 0.6 : 1 }}>
                  <i className="mdi mdi-file-check" /> Fetch Report
                </button>
              </div>
            </div>
          </div>

          {/* ── Stats Card ── */}
          {hasFetched ? (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
                <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                  <i className="mdi mdi-chart-pie" />
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
                  Loan Report — {vfcName} on {date}
                </span>
              </div>
              <div style={{ padding: "24px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                {[
                  { label: "Individual Loans", value: loanData.individualLoans, icon: "mdi-account-outline" },
                  { label: "Group Loans", value: loanData.groupLoans, icon: "mdi-account-group-outline" },
                  { label: "Total Loans", value: loanData.totalLoans, icon: "mdi-sigma" },
                ].map(({ label, value, icon }) => (
                  <div key={label} style={{ padding: "16px", background: "#F7FAFC", borderRadius: 10, border: "1px solid #EDF2F7", textAlign: "center" }}>
                    <div style={{ width: 44, height: 44, background: "linear-gradient(135deg,#1B5E20,#2E7D32)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
                      <i className={`mdi ${icon}`} style={{ fontSize: 22, color: "#fff" }} />
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#1A202C" }}>{value || 0}</div>
                    <div style={{ fontSize: 12, color: "#718096", marginTop: 4 }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", padding: "48px 24px", textAlign: "center" }}>
              <div style={{ width: 60, height: 60, background: "#E8F5E9", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <i className="mdi mdi-chart-bar" style={{ fontSize: 28, color: "#2E7D32" }} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>Select filters above</div>
              <div style={{ fontSize: 12, color: "#A0AEC0" }}>Choose a VFC and date, then click Fetch Report</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DailyLoanReport;
